import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './context/AuthContext';
import { StudentProvider } from './context/StudentContext';
import { ProtectedRoute } from './components/layout/ProtectedRoute';
import { LoadingScreen } from './components/layout/LoadingScreen';
import { AppLayout } from './components/layout/AppLayout';
import { SEO } from './components/SEO';
import { MatomoPageTracker } from './components/MatomoPageTracker';

// ── Lazy-loaded pages ──────────────────────────────────────────────────────────
const WelcomePage       = lazy(() => import('./pages/LandingPage'));
const LoginPage         = lazy(() => import('./pages/LoginPage'));
const VerifyEmailPage   = lazy(() => import('./pages/VerifyEmailPage'));

// Parent-scoped pages (parent is authenticated, no child required)
const ChildSelectPage   = lazy(() => import('./pages/ChildSelectPage'));
const AddChildPage      = lazy(() => import('./pages/AddChildPage'));
const ParentDashboard   = lazy(() => import('./pages/ParentDashboard'));
const ProfilePage       = lazy(() => import('./pages/ProfilePage'));

// Child-scoped pages (require an active child selection)
const DashboardPage     = lazy(() => import('./pages/DashboardPage'));
const CurriculumPage    = lazy(() => import('./pages/CurriculumPage'));
const LessonPage        = lazy(() => import('./pages/LessonPage'));
const ProgressPage      = lazy(() => import('./pages/ProgressPage'));
const AITutorPage       = lazy(() => import('./pages/AITutorPage'));

// Role-gated
const EditorDashboard   = lazy(() => import('./pages/EditorDashboard'));
const NotFoundPage      = lazy(() => import('./pages/NotFoundPage'));

// Legal pages — public, no auth required
const PrivacyPage       = lazy(() => import('./pages/PrivacyPage'));
const TermsPage         = lazy(() => import('./pages/TermsPage'));
const CookiesPage       = lazy(() => import('./pages/CookiesPage'));
const SafeguardingPage  = lazy(() => import('./pages/SafeguardingPage'));

export default function App() {
  return (
    <Router>
      <SEO />
      <MatomoPageTracker />
      <AuthProvider>
        <StudentProvider>
          <Suspense fallback={<LoadingScreen />}>
            <Routes>
              {/* ── Public ──────────────────────────────────────────────── */}
              <Route path="/"             element={<WelcomePage />} />
              <Route path="/login"        element={<LoginPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/privacy"      element={<PrivacyPage />} />
              <Route path="/terms"        element={<TermsPage />} />
              <Route path="/cookies"      element={<CookiesPage />} />
              <Route path="/safeguarding" element={<SafeguardingPage />} />

              {/* ── Authenticated (no PIN required) ──────────────────────
                  child-select, add-child, and profile don't need a PIN — the
                  parent just signed in with their email/password/Google. */}
              <Route element={<ProtectedRoute />}>
                <Route path="/child-select"     element={<ChildSelectPage />} />
                <Route path="/parent/add-child" element={<AddChildPage />} />
                <Route path="/profile"          element={<ProfilePage />} />
              </Route>

              {/* ── Parent PIN required ───────────────────────────────────
                  The parent dashboard needs the PIN verified this session. */}
              <Route element={<ProtectedRoute requireParentPin />}>
                <Route path="/parent" element={<ParentDashboard />} />
              </Route>

              {/* ── Child-scoped routes (require active child) ────────────
                  If no child is selected the user is bounced to /child-select. */}
              <Route element={<ProtectedRoute requireChild />}>
                <Route element={<AppLayout />}>
                  <Route path="/dashboard"        element={<DashboardPage />} />
                  <Route path="/curriculum"       element={<CurriculumPage />} />
                  <Route path="/lesson/:lessonId" element={<LessonPage />} />
                  <Route path="/progress"         element={<ProgressPage />} />
                  <Route path="/ai-tutor"         element={<AITutorPage />} />
                </Route>
              </Route>

              {/* ── Role-gated ───────────────────────────────────────────── */}
              <Route element={<ProtectedRoute role="editor" />}>
                <Route path="/editor/*" element={<EditorDashboard />} />
              </Route>

              {/* ── Fallbacks ────────────────────────────────────────────── */}
              <Route path="/onboarding" element={<Navigate to="/parent/add-child" replace />} />
              <Route path="/404"        element={<NotFoundPage />} />
              <Route path="*"           element={<Navigate to="/404" replace />} />
            </Routes>
          </Suspense>

          <Toaster
            position="bottom-center"
            toastOptions={{
              style: {
                fontFamily: 'Plus Jakarta Sans, system-ui, sans-serif',
                borderRadius: '1rem',
                fontWeight: '600',
              },
              success: { iconTheme: { primary: '#8fd400', secondary: '#121f00' } },
              error:   { iconTheme: { primary: '#ba1a1a', secondary: '#ffffff' } },
            }}
          />
        </StudentProvider>
      </AuthProvider>
    </Router>
  );
}
